/**
 * Weather Legend Content
 * Displays DTN weather layer legends
 */

import React from 'react';
import {
  WeatherLegend,
  WeatherColorTableItem,
} from '../../WeatherLegend/types';

interface WeatherLegendContentProps {
  legend: WeatherLegend[];
}

interface LegendItemProps {
  item: WeatherColorTableItem;
  type: WeatherLegend['colorTableType'];
  unit?: string;
}

const LegendItem: React.FC<LegendItemProps> = ({ item, type, unit }) => {
  const getLabel = () => {
    if (type === 'category' && item.category) {
      return item.category;
    }
    if (item.value !== null && item.value !== undefined) {
      return `${item.value}${unit ? ` ${unit}` : ''}`;
    }
    return '';
  };

  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div
        className="h-4 w-4 rounded-sm border border-border shrink-0"
        style={{ backgroundColor: item.color }}
      />
      <span className="text-xs text-muted-foreground truncate">
        {getLabel()}
      </span>
    </div>
  );
};

const RangeLegend: React.FC<{ legend: WeatherLegend }> = ({ legend }) => {
  const colors = legend.colorTable.map((item) => item.color);
  const values = legend.colorTable
    .filter((item) => item.value !== null && item.value !== undefined)
    .map((item) => item.value as number);

  const minValue = values.length > 0 ? Math.min(...values) : null;
  const maxValue = values.length > 0 ? Math.max(...values) : null;

  const gradientStyle =
    colors.length > 1
      ? { background: `linear-gradient(to right, ${colors.join(', ')})` }
      : { backgroundColor: colors[0] || '#ccc' };

  // Generate intermediate tick values for better readability
  const ticks = React.useMemo(() => {
    if (minValue === null || maxValue === null || minValue === maxValue)
      return [];

    const range = maxValue - minValue;
    const tickCount = 3; // intermediate ticks (total labels = tickCount + 2)
    const result: { value: number; position: number }[] = [];

    for (let i = 1; i <= tickCount; i++) {
      const fraction = i / (tickCount + 1);
      const value = minValue + range * fraction;
      // Round to reasonable precision
      const rounded =
        range > 100 ? Math.round(value) : Math.round(value * 10) / 10;
      result.push({ value: rounded, position: fraction * 100 });
    }

    return result;
  }, [minValue, maxValue]);

  const formatValue = (v: number) =>
    `${v}${legend.unit ? ` ${legend.unit}` : ''}`;

  return (
    <div className="space-y-1">
      <div
        className="h-3 w-full rounded-sm border border-border"
        style={gradientStyle}
      />
      {(minValue !== null || maxValue !== null) && (
        <div className="relative flex justify-between text-xs text-muted-foreground">
          <span>{minValue !== null ? formatValue(minValue) : ''}</span>
          {ticks.map((tick) => (
            <span
              key={tick.position}
              className="absolute text-center"
              style={{
                left: `${tick.position}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {tick.value}
            </span>
          ))}
          <span>{maxValue !== null ? formatValue(maxValue) : ''}</span>
        </div>
      )}
    </div>
  );
};

const DiscreteLegend: React.FC<{ legend: WeatherLegend }> = ({ legend }) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
    {legend.colorTable.map((item, index) => (
      <LegendItem
        key={index}
        item={item}
        type={legend.colorTableType}
        unit={legend.unit}
      />
    ))}
  </div>
);

const CategoryLegend: React.FC<{ legend: WeatherLegend }> = ({ legend }) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
    {legend.colorTable.map((item, index) => (
      <LegendItem key={index} item={item} type={legend.colorTableType} />
    ))}
  </div>
);

const SingleLegend: React.FC<{ legend: WeatherLegend }> = ({ legend }) => {
  switch (legend.colorTableType) {
    case 'range':
      return <RangeLegend legend={legend} />;
    case 'discrete':
      return <DiscreteLegend legend={legend} />;
    case 'category':
      return <CategoryLegend legend={legend} />;
    default:
      return <DiscreteLegend legend={legend} />;
  }
};

export const WeatherLegendContent: React.FC<WeatherLegendContentProps> = ({
  legend,
}) => {
  return (
    <div className="space-y-2">
      {legend.map((legendItem, index) => (
        <div key={index} className="space-y-1">
          {legendItem.label && legend.length > 1 && (
            <div className="text-xs font-medium text-foreground">
              {legendItem.label}
            </div>
          )}
          <SingleLegend legend={legendItem} />
        </div>
      ))}
    </div>
  );
};
